import { ListFailedTransactionsForAdminQuery } from "../api/generated/models/ListFailedTransactionsForAdminQuery";
import { FailedTransactionSource } from "../api/generated/models/FailedTransactionSource";
import { FailedTransactionStatus } from "../api/generated/models/FailedTransactionStatus";
import { kronerInputToOre } from "./adminTransactionsQuery";
import {
  dateToCopenhagenStartOfDayUTC,
  dateToCopenhagenEndOfDayUTC,
} from "./dateFilters";

export type StatusFilter = "Failed" | "Resolved" | "All";

export interface AdminFailedTransactionsFilterDraft {
  startDate: string; // yyyy-mm-dd or ""
  endDate: string; // yyyy-mm-dd or ""
  sources: FailedTransactionSource[];
  errorCodes: string[];
  minAmountKr: string;
  maxAmountKr: string;
  identity: string; // email or GUID
  merchantSearch: string;
  status: StatusFilter;
}

export const emptyFailedFilterDraft: AdminFailedTransactionsFilterDraft = {
  startDate: "",
  endDate: "",
  sources: [],
  errorCodes: [],
  minAmountKr: "",
  maxAmountKr: "",
  identity: "",
  merchantSearch: "",
  status: "Failed",
};

interface BuildQueryParams {
  filters: AdminFailedTransactionsFilterDraft;
  pageNumber: number;
  pageSize: number;
}

export const buildAdminFailedTransactionsQuery = ({
  filters,
  pageNumber,
  pageSize,
}: BuildQueryParams): ListFailedTransactionsForAdminQuery => {
  const identity = filters.identity.trim();
  const merchantSearch = filters.merchantSearch.trim();
  return {
    pagination: { pageNumber, pageSize },
    from: filters.startDate
      ? dateToCopenhagenStartOfDayUTC(filters.startDate)
      : undefined,
    to: filters.endDate
      ? dateToCopenhagenEndOfDayUTC(filters.endDate)
      : undefined,
    sources: filters.sources.length > 0 ? filters.sources : undefined,
    errorCodes: filters.errorCodes.length > 0 ? filters.errorCodes : undefined,
    minAmount: kronerInputToOre(filters.minAmountKr),
    maxAmount: kronerInputToOre(filters.maxAmountKr),
    identity: identity !== "" ? identity : undefined,
    merchantSearch: merchantSearch !== "" ? merchantSearch : undefined,
    status:
      filters.status === "All"
        ? undefined
        : filters.status === "Failed"
        ? FailedTransactionStatus.FAILED
        : FailedTransactionStatus.RESOLVED,
  };
};

const VALID_FAILED_SOURCES = new Set<string>(
  Object.values(FailedTransactionSource)
);
const VALID_STATUSES = new Set<string>(["Failed", "Resolved", "All"]);

// Serialize applied filters + 1-based page (as the page uses internally) into
// URL search params. Empty values, page 1 and the default status are omitted.
export const failedFiltersToSearchParams = (
  filters: AdminFailedTransactionsFilterDraft,
  page: number
): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.startDate) params.set("startDate", filters.startDate);
  if (filters.endDate) params.set("endDate", filters.endDate);
  if (filters.sources.length > 0) params.set("sources", filters.sources.join(","));
  if (filters.errorCodes.length > 0) params.set("errorCodes", filters.errorCodes.join(","));
  if (filters.minAmountKr.trim() !== "") params.set("minAmount", filters.minAmountKr.trim());
  if (filters.maxAmountKr.trim() !== "") params.set("maxAmount", filters.maxAmountKr.trim());
  if (filters.identity.trim() !== "") params.set("identity", filters.identity.trim());
  if (filters.merchantSearch.trim() !== "") params.set("merchantSearch", filters.merchantSearch.trim());
  if (filters.status !== "Failed") params.set("status", filters.status);
  if (page > 1) params.set("page", String(page));
  return params;
};

// Parse URL search params into a filter draft + 1-based page.
// Unknown sources/status are dropped (status falls back to "Failed"); invalid page falls back to 1.
export const searchParamsToFailedFilters = (
  params: URLSearchParams
): { filters: AdminFailedTransactionsFilterDraft; page: number } => {
  const split = (key: string) =>
    (params.get(key) ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s !== "");
  const sources = split("sources").filter(
    (s): s is FailedTransactionSource => VALID_FAILED_SOURCES.has(s)
  );
  const statusRaw = params.get("status") ?? "";
  const pageRaw = parseInt(params.get("page") ?? "1", 10);
  return {
    filters: {
      startDate: params.get("startDate") ?? "",
      endDate: params.get("endDate") ?? "",
      sources,
      errorCodes: split("errorCodes"),
      minAmountKr: params.get("minAmount") ?? "",
      maxAmountKr: params.get("maxAmount") ?? "",
      identity: params.get("identity") ?? "",
      merchantSearch: params.get("merchantSearch") ?? "",
      status: VALID_STATUSES.has(statusRaw) ? (statusRaw as StatusFilter) : "Failed",
    },
    page: Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw : 1,
  };
};
